import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  DatePicker,
  Select,
  Space,
  Button,
  Tooltip,
  Modal,
  Form,
  Tag,
  Spin,
  Empty,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getGanttData,
  reschedule,
  cancelSchedule,
  getAllMachines,
} from "../../api/schedulingApi";
import checkTokenExp from "../../utils/checkTokenExp";
import "./GanttChart.css";

const { RangePicker } = DatePicker;

/**
 * Gantt Chart — machine-wise visual timeline.
 *
 * Data shape from API Guide §7.8:
 *  { period: { start, end },
 *    machines: [{ machine_id, machine_name, machine_code, machine_type,
 *                 capacity, schedules: [{ id, title, item_name, start, end,
 *                 estimated_duration, status, stage, stage_code, color,
 *                 has_conflict, priority }] }] }
 */
const GanttChart = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [ganttData, setGanttData] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ]);

  // Reschedule
  const [rescheduleModal, setRescheduleModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [form] = Form.useForm();

  const ganttRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetchMachines();
  }, []);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) fetchGanttData();
  }, [dateRange, selectedMachines]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }
  checkTokenExp(accessToken);

  const fetchMachines = async () => {
    try {
      const data = await getAllMachines({ status: "operational" });
      setMachines(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to load machines", error);
    }
  };

  const fetchGanttData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange[0].format("YYYY-MM-DD"),
        end_date: dateRange[1].format("YYYY-MM-DD"),
      };
      if (selectedMachines.length > 0) {
        params.machine_ids = selectedMachines.join(",");
      }
      const data = await getGanttData(params);
      setGanttData(data.machines || []);
    } catch (error) {
      toast.error("Failed to load schedule data");
    }
    setLoading(false);
  };

  // ── Navigation ─────────────────────────────────────────────
  const handlePrevWeek = () => {
    setDateRange([
      dateRange[0].subtract(7, "day"),
      dateRange[1].subtract(7, "day"),
    ]);
  };

  const handleNextWeek = () => {
    setDateRange([
      dateRange[0].add(7, "day"),
      dateRange[1].add(7, "day"),
    ]);
  };

  const handleToday = () => {
    setDateRange([dayjs().startOf("week"), dayjs().endOf("week")]);
  };

  // ── Reschedule ─────────────────────────────────────────────
  const handleScheduleClick = (schedule) => {
    setSelectedSchedule(schedule);
    form.setFieldsValue({
      start_time: dayjs(schedule.start),
      machine_id: schedule.machine_id,
    });
    setRescheduleModal(true);
  };

  const handleReschedule = async (values) => {
    setRescheduleLoading(true);
    try {
      await reschedule(selectedSchedule.id, {
        start_time: values.start_time.toISOString(),
        machine_id: values.machine_id,
      });
      toast.success("Rescheduled successfully");
      setRescheduleModal(false);
      setSelectedSchedule(null);
      form.resetFields();
      fetchGanttData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Reschedule failed"
      );
    }
    setRescheduleLoading(false);
  };

  const handleCancel = async () => {
    if (!selectedSchedule) return;
    try {
      await cancelSchedule(selectedSchedule.id);
      toast.success("Schedule cancelled");
      setRescheduleModal(false);
      setSelectedSchedule(null);
      fetchGanttData();
    } catch (error) {
      toast.error("Failed to cancel schedule");
    }
  };

  // ── Gantt geometry ─────────────────────────────────────────
  const days = [];
  let cur = dateRange[0].startOf("day");
  const end = dateRange[1].endOf("day");
  while (cur.isBefore(end) || cur.isSame(end, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }

  const totalMinutes = days.length * 24 * 60;

  const getBarStyle = (schedule) => {
    const schedStart = dayjs(schedule.start);
    const schedEnd = dayjs(schedule.end);
    const rangeStart = dateRange[0].startOf("day");

    const startMinutes = schedStart.diff(rangeStart, "minute");
    const durationMinutes = schedEnd.diff(schedStart, "minute");

    const leftPct = Math.max(0, (startMinutes / totalMinutes) * 100);
    const widthPct = Math.max(
      0.5,
      Math.min(100 - leftPct, (durationMinutes / totalMinutes) * 100)
    );

    return {
      left: `${leftPct}%`,
      width: `${widthPct}%`,
      backgroundColor: schedule.color || "#1890ff",
    };
  };

  const getStatusClass = (schedule) => {
    const classes = ["gantt-bar"];
    if (schedule.status) classes.push(schedule.status);
    if (schedule.has_conflict) classes.push("conflict");
    return classes.join(" ");
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <Card
      title="Production Schedule — Gantt View"
      extra={
        <Space wrap>
          <Button icon={<LeftOutlined />} onClick={handlePrevWeek} size="small">
            Prev
          </Button>
          <Button onClick={handleToday} size="small">
            Today
          </Button>
          <Button icon={<RightOutlined />} onClick={handleNextWeek} size="small">
            Next
          </Button>
          <RangePicker
            value={dateRange}
            onChange={(val) => val && setDateRange(val)}
            format="DD MMM YYYY"
            size="small"
          />
          <Select
            mode="multiple"
            placeholder="Filter machines"
            style={{ minWidth: 180 }}
            value={selectedMachines}
            onChange={setSelectedMachines}
            allowClear
            maxTagCount={2}
            size="small"
          >
            {machines.map((m) => (
              <Select.Option key={m.id} value={m.id}>
                {m.name} ({m.code})
              </Select.Option>
            ))}
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchGanttData}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div className="gantt-container" ref={ganttRef}>
          {/* ── Header row ─────────────────────────────────── */}
          <div className="gantt-header">
            <div className="gantt-machine-column">Machine</div>
            <div className="gantt-timeline">
              {days.map((day) => (
                <div key={day.format("YYYY-MM-DD")} className="gantt-day">
                  <div
                    className={`gantt-day-header ${
                      day.isSame(dayjs(), "day") ? "gantt-today" : ""
                    }`}
                  >
                    {day.format("ddd DD MMM")}
                  </div>
                  <div className="gantt-hours">
                    {[6, 12, 18].map((h) => (
                      <span key={h} className="gantt-hour-mark">
                        {h}:00
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Machine rows ───────────────────────────────── */}
          {ganttData.map((machine) => (
            <div key={machine.machine_id} className="gantt-row">
              <div className="gantt-machine-column">
                <div className="machine-name">{machine.machine_name}</div>
                <div className="machine-code">{machine.machine_code}</div>
                {machine.machine_type && (
                  <Tag style={{ fontSize: 10, marginTop: 2 }}>
                    {machine.machine_type}
                  </Tag>
                )}
              </div>
              <div className="gantt-timeline gantt-timeline-row">
                {/* Day grid lines */}
                {days.map((day) => (
                  <div
                    key={day.format("YYYY-MM-DD")}
                    className={`gantt-day-cell ${
                      day.isSame(dayjs(), "day") ? "gantt-today-cell" : ""
                    }`}
                  />
                ))}

                {/* Schedule bars */}
                {(machine.schedules || []).map((schedule) => (
                  <Tooltip
                    key={schedule.id}
                    title={
                      <div style={{ fontSize: 12 }}>
                        <div>
                          <strong>{schedule.title}</strong>
                        </div>
                        {schedule.item_name && (
                          <div>{schedule.item_name}</div>
                        )}
                        <div>Stage: {schedule.stage}</div>
                        <div>
                          {dayjs(schedule.start).format("DD MMM HH:mm")} –{" "}
                          {dayjs(schedule.end).format("HH:mm")}
                        </div>
                        {schedule.estimated_duration && (
                          <div>
                            Duration: {Math.round(schedule.estimated_duration / 60)}h{" "}
                            {schedule.estimated_duration % 60}m
                          </div>
                        )}
                        <div>Status: {schedule.status}</div>
                        {schedule.has_conflict && (
                          <Tag color="red" style={{ marginTop: 4 }}>
                            CONFLICT
                          </Tag>
                        )}
                        {schedule.priority && (
                          <Tag
                            color={
                              schedule.priority === "urgent"
                                ? "red"
                                : schedule.priority === "high"
                                ? "orange"
                                : "blue"
                            }
                            style={{ marginTop: 4 }}
                          >
                            {schedule.priority.toUpperCase()}
                          </Tag>
                        )}
                      </div>
                    }
                  >
                    <div
                      className={getStatusClass(schedule)}
                      style={getBarStyle(schedule)}
                      onClick={() => handleScheduleClick(schedule)}
                    >
                      <span className="gantt-bar-text">
                        {schedule.title}
                      </span>
                      {schedule.priority === "urgent" && (
                        <span className="priority-indicator">!</span>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          ))}

          {ganttData.length === 0 && !loading && (
            <div className="gantt-empty">
              <Empty description="No schedules found for this period" />
            </div>
          )}
        </div>
      </Spin>

      {/* ── Reschedule Modal ───────────────────────────────── */}
      <Modal
        title={`Reschedule: ${selectedSchedule?.title || ""}`}
        open={rescheduleModal}
        onCancel={() => {
          setRescheduleModal(false);
          setSelectedSchedule(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} onFinish={handleReschedule} layout="vertical">
          {selectedSchedule && (
            <div style={{ marginBottom: 16 }}>
              <Tag>{selectedSchedule.stage}</Tag>
              <Tag
                color={
                  selectedSchedule.status === "scheduled"
                    ? "cyan"
                    : selectedSchedule.status === "in_progress"
                    ? "blue"
                    : "default"
                }
              >
                {selectedSchedule.status}
              </Tag>
              {selectedSchedule.has_conflict && (
                <Tag color="red">CONFLICT</Tag>
              )}
            </div>
          )}

          <Form.Item
            name="start_time"
            label="New Start Time"
            rules={[{ required: true, message: "Select a start time" }]}
          >
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item name="machine_id" label="Machine (optional)">
            <Select
              allowClear
              placeholder="Keep current machine"
              showSearch
              optionFilterProp="children"
            >
              {machines.map((m) => (
                <Select.Option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={rescheduleLoading}
              >
                Reschedule
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleCancel}
              >
                Cancel Schedule
              </Button>
              <Button onClick={() => setRescheduleModal(false)}>
                Close
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default GanttChart;
